import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

/**
 * Download / share a file. Works in both browser and Capacitor WebView:
 * - Browser: triggers <a> click for download
 * - Capacitor: saves to cache dir and opens system share sheet
 */
export async function downloadFile(opts: {
  filename: string
  data: string      // base64 OR raw text (if mimeType is text/*)
  mimeType: string
  isBase64?: boolean
}) {
  const { filename, data, mimeType, isBase64 = false } = opts

  if (Capacitor.isNativePlatform()) {
    // Encode text as base64 if needed
    const base64 = isBase64 ? data : btoa(unescape(encodeURIComponent(data)))
    await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache })
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache })
    await Share.share({ url: uri, title: filename })
  } else {
    // Standard browser download
    let href: string
    if (isBase64) {
      href = `data:${mimeType};base64,${data}`
    } else {
      href = URL.createObjectURL(new Blob([data], { type: mimeType }))
    }
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    a.click()
    if (!isBase64) setTimeout(() => URL.revokeObjectURL(href), 10_000)
  }
}

/**
 * Download / share a blob (e.g. from axios responseType:'blob').
 */
export async function downloadBlob(blob: Blob, filename: string) {
  if (Capacitor.isNativePlatform()) {
    const base64 = await blobToBase64(blob)
    await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache })
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache })
    await Share.share({ url: uri, title: filename })
  } else {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      // strip "data:...;base64," prefix
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * On Capacitor: share a PNG data URL (from canvas.toDataURL())
 * On browser: trigger anchor download
 */
export async function downloadDataUrl(dataUrl: string, filename: string) {
  if (Capacitor.isNativePlatform()) {
    const base64 = dataUrl.split(',')[1] ?? ''
    await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache })
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache })
    await Share.share({ url: uri, title: filename })
  } else {
    const a = document.createElement('a')
    a.href = dataUrl
    a.download = filename
    a.click()
  }
}
